/**
 * headlight-sample-scanning — Scan Samples (move scanned samples into a location, one at a time or together)
 * for any pict app, plus the location tree and the reception status rule it uses.
 *
 *     const HeadlightSampleScanningBundle = require('headlight-sample-scanning');
 *     const tmpScanning = new HeadlightSampleScanningBundle(pict).attach();
 *     tmpScanning.connect({ notify, printLabel, camera });
 *     tmpScanning.open({ DestinationAddress: '#Somewhere' });
 *     tmpScanning.listen(pict.providers.HeadlightQR);   // scans while the panel is open; close() stops listening
 */

const SampleScanningProvider = require('./providers/Pict-Provider-SampleScanning.js');
const SampleScanningView = require('./views/PictView-SampleScanning.js');

class HeadlightSampleScanningBundle
{
	/**
	 * @param {any} pPict
	 */
	constructor(pPict)
	{
		this.pict = pPict;
	}

	/**
	 * Register the provider and view. Safe to call more than once.
	 *
	 * @param {Object} [pOptions]
	 * @param {Object} [pOptions.SampleScanning] - Provider options (`Store`, `Storage`, `RemoveFromCurrentLocation`).
	 * @param {Object} [pOptions.View] - View options (`DefaultDestinationAddress`).
	 * @return {SampleScanningProvider}
	 */
	attach(pOptions)
	{
		const tmpOptions = pOptions || {};
		if (!this.pict.providers.SampleScanning)
		{
			this.pict.addProviderSingleton('SampleScanning', tmpOptions.SampleScanning || {}, SampleScanningProvider);
		}
		if (!this.pict.views.SampleScanning)
		{
			this.pict.addViewSingleton('SampleScanning', tmpOptions.View || {}, SampleScanningView);
			// Attached after the app's first CSS injection, so the view's styles need injecting now.
			if ((typeof document !== 'undefined') && this.pict.CSSMap && (typeof this.pict.CSSMap.injectCSS === 'function'))
			{
				this.pict.CSSMap.injectCSS();
			}
		}
		return this.pict.providers.SampleScanning;
	}

	/**
	 * Register a second, separate cart under its own name: its own state, element IDs and templates, so it can be on
	 * screen beside the panels' Scan Samples (e.g. a sample's Location tab). Safe to call more than once.
	 *
	 *     const tmpCart = new HeadlightSampleScanningBundle(pict).attachInstance('SampleLocationCart', { SampleScanning: { Storage } });
	 *     tmpCart.connect({ notify, moved });
	 *     tmpCart.open({ DestinationAddress: '#Somewhere', SaveEachScan: false });
	 *
	 * @param {string} pName - the provider and view name, and the element/template prefix
	 * @param {Object} [pOptions]
	 * @param {Object} [pOptions.SampleScanning] - Provider options (`Store`, `Storage`, `RemoveFromCurrentLocation`).
	 * @return {SampleScanningProvider}
	 */
	attachInstance(pName, pOptions)
	{
		const tmpOptions = pOptions || {};
		if (!this.pict.providers[pName])
		{
			this.pict.addProviderSingleton(pName, Object.assign({}, tmpOptions.SampleScanning || {},
				{ ProviderIdentifier: pName, ViewHash: pName, IDPrefix: pName, ScanHandlerHash: pName }), SampleScanningProvider);
		}
		if (!this.pict.views[pName])
		{
			this.pict.addViewSingleton(pName, { ProviderHash: pName, IDPrefix: pName }, SampleScanningView);
			if ((typeof document !== 'undefined') && this.pict.CSSMap && (typeof this.pict.CSSMap.injectCSS === 'function'))
			{
				this.pict.CSSMap.injectCSS();
			}
		}
		return this.pict.providers[pName];
	}
}

module.exports = HeadlightSampleScanningBundle;
module.exports.SampleScanningProvider = SampleScanningProvider;
module.exports.SampleScanningView = SampleScanningView;
module.exports.AppSampleScanStore = require('./Sample-Scan-Store.js');
module.exports.LocationTree = require('./Location-Tree.js');
module.exports.SampleScanStatus = require('./Sample-Scan-Status.js');
module.exports.moveSampleToLocation = require('./Sample-Move.js').moveSampleToLocation;
module.exports.ActionCards = require('./Sample-Scan-Action-Cards.js').ACTION_CARDS;
